import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, MapPin, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { erpApi } from "../services/erpApi";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  center_type: "clinic",
  scale: "small",
  city: "",
  state: "",
  map_latitude: "",
  map_longitude: "",
  google_maps_url: "",
  logo_url: "",
  invoice_prefix: "INV",
  invoice_footer: "Computer generated invoice",
  gst_number: "",
  whatsapp_number: "",
  upi_id: "",
  theme_color: "#0F4C3A",
};

export default function Settings() {
  const auth = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.clinic) {
      setForm({ ...emptyForm, ...auth.clinic });
    }
  }, [auth.clinic]);

  const mapsUrl = () => {
    if (form.google_maps_url) return form.google_maps_url;
    if (form.map_latitude && form.map_longitude) {
      return `https://www.google.com/maps?q=${encodeURIComponent(`${form.map_latitude},${form.map_longitude}`)}`;
    }
    const query = [form.name, form.address, form.city, form.state].filter(Boolean).join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  const openMaps = () => {
    if (!form.address && !form.map_latitude && !form.google_maps_url) {
      toast.error("Add address or map coordinates first");
      return;
    }
    window.open(mapsUrl(), "_blank", "noopener,noreferrer");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.address) {
      toast.error("Center name, email and address are required");
      return;
    }
    setLoading(true);
    try {
      const response = await erpApi.updateClinic(form);
      auth.updateClinicSession(response.data);
      toast.success("Clinic settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-manrope text-2xl font-bold">Clinic Settings</h1>
        <p className="text-sm text-hospital-text-muted">Manage clinic profile, invoice branding, payment details and Google Maps location.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="border border-gray-200">
          <CardHeader><CardTitle className="text-base">Clinic Profile</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Center Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Center Type</Label>
              <Select value={form.center_type} onValueChange={(value) => setForm({ ...form, center_type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clinic">Clinic</SelectItem>
                  <SelectItem value="diagnostic_center">Diagnostic Center</SelectItem>
                  <SelectItem value="pathology_lab">Pathology Lab</SelectItem>
                  <SelectItem value="care_center">Care Center</SelectItem>
                  <SelectItem value="ngo_hospital">NGO Hospital</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Center Size</Label>
              <Select value={form.scale} onValueChange={(value) => setForm({ ...form, scale: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="large">Large / Multi Department</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Full Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="flex justify-end md:col-span-2">
              <Button className="btn-primary" disabled={loading}>
                <Save className="mr-2 h-4 w-4" />
                {loading ? "Saving..." : "Save Clinic Settings"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Invoice & Branding</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input placeholder="https://..." value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Invoice Prefix</Label>
                  <Input placeholder="INV, ATDC, BILL" value={form.invoice_prefix} onChange={(e) => setForm({ ...form, invoice_prefix: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Theme Color</Label>
                  <Input type="color" value={form.theme_color || "#0F4C3A"} onChange={(e) => setForm({ ...form, theme_color: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Invoice Footer</Label>
                <Textarea value={form.invoice_footer} onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })} />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Payment & Tax</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>GST Number</Label>
                <Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>UPI ID</Label>
                <Input placeholder="clinic@upi" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>WhatsApp Number</Label>
                <Input placeholder="919999999999" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Google Maps Location</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Latitude</Label>
                  <Input placeholder="24.5854" value={form.map_latitude} onChange={(e) => setForm({ ...form, map_latitude: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Longitude</Label>
                  <Input placeholder="73.7125" value={form.map_longitude} onChange={(e) => setForm({ ...form, map_longitude: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Google Maps Link</Label>
                <Input placeholder="Paste Google Maps share link" value={form.google_maps_url} onChange={(e) => setForm({ ...form, google_maps_url: e.target.value })} />
              </div>
              <Button type="button" variant="outline" className="w-full" onClick={openMaps}>
                <MapPin className="mr-2 h-4 w-4" />
                Open in Google Maps
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-sm text-hospital-text-muted">If coordinates are empty, Maps will search using center name and address.</p>
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardHeader><CardTitle className="text-base">Account Summary</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm text-hospital-text-secondary">
              <p><span className="font-semibold text-hospital-text-main">Admin:</span> {auth.user?.name || "-"}</p>
              <p><span className="font-semibold text-hospital-text-main">Role:</span> <span className="capitalize">{auth.user?.role || "-"}</span></p>
              <p><span className="font-semibold text-hospital-text-main">Clinic ID:</span> {auth.clinic?.id || "-"}</p>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
